<template>
  <footer
    class="app-footer mt-auto"
    :class="{ 'has-entered': hasEntered, 'in-view': inView }"
    ref="footerRef"
    @mousemove="onMouseMove"
    @mouseleave="onMouseLeave"
  >
    <!-- Décor : animations CSS (transform/opacity), en pause hors écran -->
    <div class="footer-decor" aria-hidden="true">
      <div class="aurora aurora--a"></div>
      <div class="aurora aurora--b"></div>
      <div class="aurora aurora--c"></div>
      <div class="decor-grid"></div>
      <div class="decor-spotlight"></div>
      <span
        v-for="(spark, i) in sparks"
        :key="i"
        class="spark"
        :style="{ left: spark.left + '%', width: spark.size + 'px', height: spark.size + 'px', animationDelay: spark.delay + 's', animationDuration: spark.duration + 's' }"
      ></span>
    </div>
    <div class="footer-topline" aria-hidden="true"></div>

    <div class="footer-inner">
      <div class="footer-main">

        <!-- Marque -->
        <div class="footer-brand reveal" style="--i: 0">
          <router-link to="/" class="brand-link" :aria-label="`${$branding.appName} - Tableau de bord`">
            <span class="brand-mark">
              <span class="brand-mark-ring"></span>
              <span class="brand-mark-core"><i class="bi bi-building-fill"></i></span>
            </span>
            <span class="brand-text">
              <span class="brand-name">{{ $branding.appName }}</span>
              <span class="brand-subtitle">ERP BTP</span>
            </span>
          </router-link>
          <p class="brand-tagline">
            Pilotage des projets, budgets et ressources de chantier, au même endroit.
          </p>
        </div>

        <!-- Navigation (mêmes règles d'accès que la Navbar) -->
        <nav v-if="isAuthenticated" class="footer-col reveal" style="--i: 1" aria-label="Navigation du pied de page">
          <h6 class="col-title">Navigation</h6>
          <ul class="col-links">
            <li v-for="link in navLinks" :key="link.to">
              <router-link :to="link.to">
                <i :class="['bi', link.icon]"></i>
                <span class="link-label">{{ link.label }}</span>
              </router-link>
            </li>
          </ul>
        </nav>

        <!-- Assistance -->
        <div v-if="isAuthenticated" class="footer-col reveal" style="--i: 2">
          <h6 class="col-title">Assistance</h6>
          <ul class="col-links">
            <li>
              <router-link to="/feedback">
                <i class="bi bi-bug"></i><span class="link-label">Signaler un problème</span>
              </router-link>
            </li>
            <li>
              <router-link to="/profile">
                <i class="bi bi-person-circle"></i><span class="link-label">Mon profil</span>
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Statut système -->
        <div class="footer-status reveal" style="--i: 3">
          <div class="status-card">
            <div class="status-clock">
              <span class="clock-time" :aria-label="currentTime">
                <span
                  v-for="(char, i) in timeChars"
                  :key="i + '-' + char"
                  class="clock-char"
                  :class="{ 'is-sep': char === ':' }"
                  aria-hidden="true"
                >{{ char }}</span>
              </span>
              <span class="clock-date">{{ currentDate }}</span>
            </div>
            <span class="status-pill" :class="isOnline ? 'is-online' : 'is-offline'" :title="isOnline ? 'Connexion active' : 'Aucune connexion réseau'">
              <span class="status-dot"></span>
              {{ isOnline ? 'En ligne' : 'Hors ligne' }}
            </span>
          </div>
        </div>
      </div>

      <!-- Barre inférieure -->
      <div class="footer-bottom reveal" style="--i: 4">
        <span class="copyright">
          &copy; {{ year }} {{ $branding.appName }}
          <span class="sep">·</span>
          Conçu par
          <a href="https://www.linkedin.com/in/a-cherifi" target="_blank" rel="noopener noreferrer" class="author-link">
            Cherifi.A <i class="bi bi-linkedin"></i>
          </a>
        </span>

        <button
          type="button"
          class="back-to-top"
          @click="scrollToTop"
          @mousemove="magnetize"
          @mouseleave="demagnetize"
          aria-label="Revenir en haut de la page"
        >
          <span>Haut de page</span>
          <span class="back-to-top-icon"><i class="bi bi-arrow-up"></i></span>
        </button>
      </div>
    </div>

    <!-- Signature géante en arrière-plan : s'illumine doucement, et sous la souris -->
    <div class="footer-wordmark" ref="wordmarkRef" aria-hidden="true">
      <span class="wm-layer wm-outline">{{ $branding.appName }}</span>
      <span class="wm-layer wm-glow">{{ $branding.appName }}</span>
      <span class="wm-layer wm-fill">{{ $branding.appName }}</span>
    </div>
  </footer>
</template>

<script>
import { computed } from 'vue'
import { useAuth } from '../composables/useAuth'

export default {
  name: 'Footer',
  setup() {
    const { isAuthenticated, isHR, isPointeur, isSupervisor, hasPermission } = useAuth()

    // Visibilité alignée sur Navbar.vue : pointeurs et conducteurs de travaux
    // n'ont ni le tableau de bord ni les projets, mais accèdent au chantier
    const navLinks = computed(() => [
      { to: '/', label: 'Tableau de bord', icon: 'bi-grid-1x2', show: !isPointeur.value && !isSupervisor.value },
      { to: '/projects', label: 'Projets', icon: 'bi-kanban', show: !isHR.value && !isPointeur.value && !isSupervisor.value },
      { to: '/site-reports', label: 'Rapport chantier', icon: 'bi-clipboard2-data', show: hasPermission('site_reports.view') },
      { to: '/photos-chantier', label: 'Photos chantier', icon: 'bi-camera', show: hasPermission('site_reports.create') },
      { to: '/notifications', label: 'Notifications', icon: 'bi-bell', show: true }
    ].filter(link => link.show))

    // Étincelles qui montent : positions fixes (pas d'aléatoire à chaque rendu)
    const sparks = Array.from({ length: 16 }, (_, i) => ({
      left: (i * 37 + 7) % 100,
      size: 2 + (i % 3),
      delay: -((i * 1.9) % 12),
      duration: 10 + ((i * 3) % 8)
    }))

    return { isAuthenticated, navLinks, sparks }
  },
  data() {
    return {
      currentTime: '',
      currentDate: '',
      year: new Date().getFullYear(),
      isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
      hasEntered: false,
      inView: false,
      timer: null
    }
  },
  computed: {
    timeChars() {
      return this.currentTime.split('');
    }
  },
  created() {
    // Non réactifs : utilisés uniquement pour le suivi de la souris
    this.observer = null;
    this.pointerFrame = null;
    this.lastPointer = null;
  },
  mounted() {
    this.updateTime();
    this.timer = setInterval(this.updateTime, 1000);
    window.addEventListener('online', this.updateOnline);
    window.addEventListener('offline', this.updateOnline);

    // Apparition au scroll + pause des animations quand le footer est hors écran
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        if (entry.isIntersecting) this.hasEntered = true;
      }, { threshold: 0.1 });
      this.observer.observe(this.$refs.footerRef);
    } else {
      this.hasEntered = true;
      this.inView = true;
    }
  },
  beforeUnmount() {
    clearInterval(this.timer);
    cancelAnimationFrame(this.pointerFrame);
    this.observer?.disconnect();
    window.removeEventListener('online', this.updateOnline);
    window.removeEventListener('offline', this.updateOnline);
  },
  methods: {
    updateTime() {
      const now = new Date();
      this.currentTime = now.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
      this.currentDate = now.toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long'
      });
    },
    updateOnline() {
      this.isOnline = navigator.onLine;
    },
    // Halo + révélation de la signature via des variables CSS, une fois par frame
    onMouseMove(e) {
      this.lastPointer = { x: e.clientX, y: e.clientY };
      if (this.pointerFrame) return;
      this.pointerFrame = requestAnimationFrame(() => {
        this.pointerFrame = null;
        const footer = this.$refs.footerRef;
        const wordmark = this.$refs.wordmarkRef;
        if (!footer || !wordmark || !this.lastPointer) return;
        const { x, y } = this.lastPointer;
        const f = footer.getBoundingClientRect();
        const w = wordmark.getBoundingClientRect();
        footer.style.setProperty('--mx', `${x - f.left}px`);
        footer.style.setProperty('--my', `${y - f.top}px`);
        footer.style.setProperty('--wx', `${x - w.left}px`);
        footer.style.setProperty('--wy', `${y - w.top}px`);
        footer.style.setProperty('--spot', '1');
      });
    },
    onMouseLeave() {
      this.$refs.footerRef?.style.setProperty('--spot', '0');
    },
    // Bouton "aimanté" : il suit légèrement le curseur
    magnetize(e) {
      const btn = e.currentTarget;
      const r = btn.getBoundingClientRect();
      btn.style.setProperty('--tx', `${(e.clientX - (r.left + r.width / 2)) * 0.25}px`);
      btn.style.setProperty('--ty', `${(e.clientY - (r.top + r.height / 2)) * 0.4}px`);
    },
    demagnetize(e) {
      e.currentTarget.style.setProperty('--tx', '0px');
      e.currentTarget.style.setProperty('--ty', '0px');
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@500;700&display=swap');

@property --angle {
  syntax: '<angle>';
  initial-value: 0deg;
  inherits: false;
}

.app-footer {
  --f-bg: #070b16;
  --f-surface: rgba(30, 41, 59, 0.45);
  --f-border: rgba(148, 163, 184, 0.12);
  --f-text: #e2e8f0;
  --f-muted: #94a3b8;
  --f-subtle: #64748b;
  --f-accent: #818cf8;
  --f-accent-strong: #6366f1;
  --f-sky: #38bdf8;
  --f-violet: #c084fc;
  --mx: 50%;
  --my: 0px;
  --wx: 50%;
  --wy: 50%;
  --spot: 0;

  position: relative;
  z-index: 10;
  margin-top: auto;
  background: linear-gradient(180deg, #0f172a 0%, var(--f-bg) 100%);
  color: var(--f-text);
  overflow: hidden;
  isolation: isolate;
}

/* Animations en pause tant que le footer n'est pas visible */
.app-footer:not(.in-view) *,
.app-footer:not(.in-view) *::before,
.app-footer:not(.in-view) *::after {
  animation-play-state: paused !important;
}

/* ---------- Décor ---------- */
.footer-decor {
  position: absolute;
  inset: 0;
  z-index: -1;
  pointer-events: none;
}

.aurora {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  will-change: transform;
  opacity: 0.55;
}

.aurora--a {
  width: 520px;
  height: 420px;
  top: -240px;
  left: -120px;
  background: radial-gradient(circle, #6366f1 0%, transparent 70%);
  animation: aurora-a 22s ease-in-out infinite alternate;
}

.aurora--b {
  width: 460px;
  height: 380px;
  top: -200px;
  left: 45%;
  background: radial-gradient(circle, #0ea5e9 0%, transparent 70%);
  opacity: 0.35;
  animation: aurora-b 26s ease-in-out infinite alternate;
}

.aurora--c {
  width: 520px;
  height: 460px;
  bottom: -300px;
  right: -140px;
  background: radial-gradient(circle, #a855f7 0%, transparent 70%);
  opacity: 0.4;
  animation: aurora-c 30s ease-in-out infinite alternate;
}

@keyframes aurora-a {
  to { transform: translate(260px, 80px) scale(1.2); }
}

@keyframes aurora-b {
  to { transform: translate(-240px, 60px) scale(0.85); }
}

@keyframes aurora-c {
  to { transform: translate(-320px, -60px) scale(1.15); }
}

.decor-grid {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(rgba(148, 163, 184, 0.16) 1px, transparent 1px);
  background-size: 22px 22px;
  mask-image: radial-gradient(ellipse at 50% 0%, black 0%, transparent 75%);
  -webkit-mask-image: radial-gradient(ellipse at 50% 0%, black 0%, transparent 75%);
}

.decor-spotlight {
  position: absolute;
  inset: 0;
  background: radial-gradient(520px circle at var(--mx) var(--my), rgba(129, 140, 248, 0.13), transparent 45%);
  opacity: var(--spot);
  transition: opacity 0.4s ease;
}

.spark {
  position: absolute;
  bottom: -10px;
  border-radius: 50%;
  background: #c7d2fe;
  box-shadow: 0 0 6px 1px rgba(129, 140, 248, 0.8);
  opacity: 0;
  animation: spark-rise linear infinite;
}

@keyframes spark-rise {
  0% { transform: translate3d(0, 0, 0); opacity: 0; }
  10% { opacity: 0.9; }
  70% { opacity: 0.5; }
  100% { transform: translate3d(30px, -520px, 0); opacity: 0; }
}

/* Ligne lumineuse en haut, avec un reflet qui la parcourt */
.footer-topline {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(129, 140, 248, 0.6) 30%, rgba(56, 189, 248, 0.5) 70%, transparent);
  overflow: hidden;
}

.footer-topline::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 220px;
  height: 100%;
  background: linear-gradient(90deg, transparent, #fff, transparent);
  animation: line-sweep 6s cubic-bezier(0.6, 0, 0.4, 1) infinite;
}

@keyframes line-sweep {
  0% { transform: translateX(-240px); }
  60%, 100% { transform: translateX(100vw); }
}

/* ---------- Apparition au scroll ---------- */
.reveal {
  opacity: 0;
  transform: translateY(24px);
  filter: blur(6px);
  transition:
    opacity 0.8s cubic-bezier(0.2, 0.8, 0.2, 1),
    transform 0.8s cubic-bezier(0.2, 0.8, 0.2, 1),
    filter 0.8s ease;
  transition-delay: calc(var(--i, 0) * 110ms);
}

.has-entered .reveal {
  opacity: 1;
  transform: none;
  filter: none;
}

/* ---------- Layout ---------- */
.footer-inner {
  position: relative;
  z-index: 1; /* devant la signature géante */
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem 2rem 1rem;
  text-shadow: 0 1px 10px rgba(7, 11, 22, 0.9); /* lisibilité au-dessus de la lueur */
}

.brand-name {
  text-shadow: none; /* texte en dégradé : l'ombre passerait au travers */
}

.footer-main {
  display: grid;
  grid-template-columns: minmax(240px, 1.6fr) 1fr 1fr minmax(260px, 1.2fr);
  gap: 2.5rem;
  align-items: start;
  padding-bottom: 1.5rem;
}

/* ---------- Marque ---------- */
.brand-link {
  display: inline-flex;
  align-items: center;
  gap: 0.85rem;
  text-decoration: none;
}

.brand-mark {
  position: relative;
  width: 48px;
  height: 48px;
  display: grid;
  place-items: center;
}

.brand-mark-ring {
  position: absolute;
  inset: -3px;
  border-radius: 16px;
  background: conic-gradient(from var(--angle), #6366f1, #38bdf8, #c084fc, #6366f1);
  animation: spin-angle 5s linear infinite;
  filter: blur(6px);
  opacity: 0.7;
  transition: opacity 0.3s ease, filter 0.3s ease;
}

.brand-mark-core {
  position: relative;
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  border-radius: 14px;
  background: linear-gradient(135deg, #4f46e5 0%, #3730a3 100%);
  color: #fff;
  font-size: 1.3rem;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.3);
  transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.brand-link:hover .brand-mark-core {
  transform: translateY(-2px) rotate(-6deg) scale(1.05);
}

.brand-link:hover .brand-mark-ring {
  opacity: 1;
  filter: blur(10px);
}

.brand-text {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
}

.brand-name {
  font-family: var(--font-display, 'Outfit', sans-serif);
  font-weight: 800;
  font-size: 1.45rem;
  letter-spacing: 0.04em;
  background: linear-gradient(110deg, #fff 0%, #c7d2fe 35%, #818cf8 50%, #c7d2fe 65%, #fff 100%);
  background-size: 250% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: text-shine 6s linear infinite;
}

@keyframes text-shine {
  from { background-position: 100% 0; }
  to { background-position: -150% 0; }
}

.brand-subtitle {
  margin-top: 0.15rem;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  color: var(--f-accent);
}

.brand-tagline {
  margin: 0.75rem 0 0;
  max-width: 320px;
  font-size: 0.875rem;
  line-height: 1.65;
  color: var(--f-muted);
}

/* ---------- Colonnes de liens ---------- */
.col-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.25rem 0 0.75rem;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--f-subtle);
}

.col-title::before {
  content: '';
  width: 14px;
  height: 2px;
  border-radius: 2px;
  background: linear-gradient(90deg, var(--f-accent), var(--f-sky));
}

.col-links {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.col-links a {
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  font-size: 0.875rem;
  color: #cbd5e1;
  text-decoration: none;
  transition: color 0.25s ease, transform 0.25s ease;
}

.col-links a i {
  width: 24px;
  height: 24px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  font-size: 0.85rem;
  color: var(--f-muted);
  background: rgba(148, 163, 184, 0.06);
  border: 1px solid rgba(148, 163, 184, 0.08);
  transition: color 0.25s ease, background 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease, transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.link-label {
  background: linear-gradient(90deg, var(--f-accent), var(--f-sky)) no-repeat left bottom / 0% 1px;
  padding-bottom: 2px;
  transition: background-size 0.35s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.col-links a:hover,
.col-links a:focus-visible {
  color: #fff;
  transform: translateX(4px);
}

.col-links a:hover i,
.col-links a:focus-visible i {
  color: #fff;
  background: linear-gradient(135deg, var(--f-accent-strong), #3730a3);
  border-color: transparent;
  box-shadow: 0 6px 16px -4px rgba(99, 102, 241, 0.7);
  transform: rotate(-8deg) scale(1.08);
}

.col-links a:hover .link-label,
.col-links a:focus-visible .link-label {
  background-size: 100% 1px;
}

.col-links a.router-link-exact-active {
  color: var(--f-accent);
}

.col-links a.router-link-exact-active i {
  color: var(--f-accent);
  border-color: rgba(129, 140, 248, 0.35);
}

/* ---------- Carte de statut ---------- */
.footer-status {
  grid-column: -2 / -1; /* reste à droite même si des colonnes sont masquées */
  display: flex;
  justify-content: flex-end;
}

.status-card {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  width: 100%;
  max-width: 310px;
  padding: 1rem 1.15rem;
  border-radius: 16px;
  background: rgba(15, 23, 42, 0.65);
  box-shadow: 0 20px 40px -20px rgba(0, 0, 0, 0.8), inset 0 1px 0 rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

/* Bordure lumineuse qui tourne */
.status-card::before {
  content: '';
  position: absolute;
  inset: 0;
  padding: 1px;
  border-radius: inherit;
  background: conic-gradient(from var(--angle), rgba(148, 163, 184, 0.12) 0deg 200deg, #818cf8 260deg, #38bdf8 300deg, rgba(148, 163, 184, 0.12) 360deg);
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  animation: spin-angle 6s linear infinite;
  pointer-events: none;
}

@keyframes spin-angle {
  to { --angle: 360deg; }
}

.status-clock {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.clock-time {
  display: inline-flex;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-weight: 700;
  font-size: 1.2rem;
  color: #f1f5f9;
  letter-spacing: 0.02em;
  overflow: hidden;
  text-shadow: 0 0 18px rgba(129, 140, 248, 0.55);
}

/* Chaque chiffre qui change "tombe" en place */
.clock-char {
  display: inline-block;
  animation: digit-drop 0.45s cubic-bezier(0.2, 0.9, 0.3, 1.2);
}

.clock-char.is-sep {
  color: var(--f-accent);
  animation: sep-blink 1s steps(1) infinite;
}

@keyframes digit-drop {
  from { transform: translateY(-80%); opacity: 0; filter: blur(2px); }
  to { transform: none; opacity: 1; filter: none; }
}

@keyframes sep-blink {
  50% { opacity: 0.35; }
}

.clock-date {
  margin-top: 0.2rem;
  font-size: 0.72rem;
  color: var(--f-muted);
}

.clock-date::first-letter {
  text-transform: uppercase;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.32rem 0.75rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
  white-space: nowrap;
}

.status-pill.is-online {
  color: #6ee7b7;
  background: rgba(16, 185, 129, 0.12);
  border: 1px solid rgba(16, 185, 129, 0.25);
  box-shadow: 0 0 20px -6px rgba(16, 185, 129, 0.6);
}

.status-pill.is-offline {
  color: #fca5a5;
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.status-dot {
  position: relative;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}

.is-online .status-dot::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: currentColor;
  animation: ping 2s cubic-bezier(0, 0, 0.2, 1) infinite;
}

@keyframes ping {
  75%, 100% { transform: scale(2.6); opacity: 0; }
}

/* ---------- Barre inférieure ---------- */
.footer-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--f-border);
}

.copyright {
  font-size: 0.8rem;
  color: var(--f-subtle);
}

.sep {
  margin: 0 0.4rem;
  opacity: 0.6;
}

.author-link {
  color: var(--f-accent);
  font-weight: 600;
  text-decoration: none;
  transition: color 0.2s ease, text-shadow 0.2s ease;
}

.author-link i {
  font-size: 0.75rem;
  margin-left: 0.15rem;
}

.author-link:hover {
  color: #fff;
  text-shadow: 0 0 12px rgba(129, 140, 248, 0.9);
}

.back-to-top {
  --tx: 0px;
  --ty: 0px;
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.3rem 0.35rem 0.3rem 0.95rem;
  border-radius: 999px;
  border: 1px solid var(--f-border);
  background: rgba(255, 255, 255, 0.03);
  color: var(--f-muted);
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transform: translate(var(--tx), var(--ty));
  transition: transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1), color 0.2s ease, border-color 0.2s ease, background 0.2s ease, box-shadow 0.3s ease;
}

.back-to-top-icon {
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: rgba(99, 102, 241, 0.18);
  color: var(--f-accent);
  overflow: hidden;
  transition: background 0.2s ease, color 0.2s ease;
}

.back-to-top:hover,
.back-to-top:focus-visible {
  color: #fff;
  border-color: rgba(129, 140, 248, 0.5);
  background: rgba(99, 102, 241, 0.12);
  box-shadow: 0 0 24px -4px rgba(99, 102, 241, 0.6);
}

.back-to-top:hover .back-to-top-icon,
.back-to-top:focus-visible .back-to-top-icon {
  background: linear-gradient(135deg, var(--f-accent-strong), var(--f-sky));
  color: #fff;
}

.back-to-top:hover .back-to-top-icon i,
.back-to-top:focus-visible .back-to-top-icon i {
  animation: arrow-loop 0.6s cubic-bezier(0.5, 0, 0.3, 1);
}

@keyframes arrow-loop {
  0% { transform: translateY(0); }
  45% { transform: translateY(-140%); }
  46% { transform: translateY(140%); }
  100% { transform: translateY(0); }
}

/* ---------- Signature géante (arrière-plan, n'occupe aucune hauteur) ---------- */
.footer-wordmark {
  position: absolute;
  z-index: 0;
  left: 50%;
  bottom: -0.17em;
  transform: translateX(-50%);
  display: grid;
  font-family: var(--font-display, 'Outfit', sans-serif);
  font-size: clamp(5rem, 17vw, 16rem);
  font-weight: 900;
  line-height: 0.8;
  letter-spacing: -0.04em;
  white-space: nowrap;
  user-select: none;
  pointer-events: none;
}

.wm-layer {
  grid-area: 1 / 1;
  color: transparent;
  -webkit-background-clip: text;
  background-clip: text;
}

.wm-outline {
  background-image: linear-gradient(180deg, rgba(148, 163, 184, 0.14) 0%, rgba(148, 163, 184, 0) 75%);
  -webkit-text-stroke: 1px rgba(148, 163, 184, 0.14);
}

/* Lueur qui "respire" : le mot s'illumine puis s'éteint lentement (opacité seule) */
.wm-glow {
  background-image: linear-gradient(100deg, #6366f1 0%, #38bdf8 40%, #c084fc 70%, #6366f1 100%);
  -webkit-mask-image: linear-gradient(180deg, #000 0%, transparent 85%);
  mask-image: linear-gradient(180deg, #000 0%, transparent 85%);
  filter: drop-shadow(0 0 24px rgba(99, 102, 241, 0.6));
  opacity: 0;
  animation: wm-breathe 7s ease-in-out infinite;
}

@keyframes wm-breathe {
  0%, 100% { opacity: 0.04; }
  50% { opacity: 0.22; }
}

/* Révélation sous la souris, gardée discrète pour ne pas gêner la lecture du texte devant */
.wm-fill {
  background-image: linear-gradient(100deg, #6366f1 0%, #38bdf8 35%, #c084fc 65%, #6366f1 100%);
  background-size: 200% 100%;
  animation: wm-flow 8s linear infinite;
  -webkit-mask-image: radial-gradient(260px circle at var(--wx) var(--wy), #000 0%, rgba(0, 0, 0, 0.35) 45%, transparent 75%);
  mask-image: radial-gradient(260px circle at var(--wx) var(--wy), #000 0%, rgba(0, 0, 0, 0.35) 45%, transparent 75%);
  opacity: calc(var(--spot) * 0.5);
  transition: opacity 0.5s ease;
}

@keyframes wm-flow {
  to { background-position: -200% 0; }
}

/* Focus clavier visible */
.app-footer a:focus-visible,
.app-footer button:focus-visible {
  outline: 2px solid var(--f-accent);
  outline-offset: 3px;
  border-radius: 6px;
}

.back-to-top:focus-visible {
  border-radius: 999px;
}

/* ---------- Responsive ---------- */
@media (max-width: 1100px) {
  .footer-main {
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
  }

  .footer-status {
    justify-content: flex-start;
  }
}

@media (max-width: 576px) {
  .footer-inner {
    padding: 1.75rem 1rem 1rem;
  }

  .footer-main {
    grid-template-columns: 1fr 1fr;
    gap: 1.75rem 1rem;
  }

  .footer-brand,
  .footer-status {
    grid-column: 1 / -1;
  }

  .status-card {
    max-width: none;
  }

  .footer-bottom {
    flex-direction: column-reverse;
    text-align: center;
  }

  .aurora {
    filter: blur(60px);
  }
}

/* Écrans tactiles : pas de survol, on laisse la signature légèrement colorée */
@media (hover: none) {
  .wm-fill {
    -webkit-mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0.35), transparent 80%);
    mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0.35), transparent 80%);
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .app-footer *,
  .app-footer *::before,
  .app-footer *::after {
    animation: none !important;
    transition: none !important;
  }

  .reveal {
    opacity: 1;
    transform: none;
    filter: none;
  }

  .spark {
    display: none;
  }
}
</style>
